import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, Mail, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2" data-testid="auth-shell">
      <div className="flex items-center justify-center p-8 lg:p-12 bg-[#FAFAF8]">
        <div className="w-full max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-[#FCEBD6] text-[#B36B15] tracking-wide">
            <Sparkles className="h-3.5 w-3.5" /> Alpha · Early Tester
          </div>
          <h1 className="mt-5 text-3xl sm:text-4xl font-semibold text-[#5A5B5C] tracking-tight">
            {title}
          </h1>
          <p className="mt-2 text-[#272525]">{subtitle}</p>
          <div className="mt-8">{children}</div>
          <div className="mt-6 text-sm text-[#5A5B5C]">{footer}</div>
        </div>
      </div>
      <div className="hidden lg:block relative bg-[#F4F5F2] overflow-hidden">
        <img
          src="https://images.pexels.com/photos/4267531/pexels-photo-4267531.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="KI-Leitstelle Training"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E1F1A]/75 via-[#1E1F1A]/30 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white max-w-md">
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#A7B584]">
            Help4Emergency · KI-Leitstelle
          </div>
          <h2 className="mt-3 text-3xl font-semibold leading-tight">
            Wer geübt hat, handelt im Ernstfall ruhiger.
          </h2>
        </div>
      </div>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/password-reset/request", { email });
      setSubmitted(true);
    } catch (e) {
      // Backend always returns 200 — only network errors land here
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Passwort vergessen?"
      subtitle="Wir senden Ihnen einen Link, mit dem Sie ein neues Passwort vergeben können."
      footer={
        <>
          Doch noch eingefallen?{" "}
          <Link
            to="/login"
            className="text-[#A7B584] hover:text-[#8C9A69] font-semibold"
            data-testid="forgot-back-to-login"
          >
            Zur Anmeldung
          </Link>
        </>
      }
    >
      {submitted ? (
        <div
          data-testid="forgot-success"
          className="rounded-xl border border-[#A7B584]/40 bg-[#F4F5F2]/60 p-6"
        >
          <CheckCircle2 className="h-6 w-6 text-[#A7B584]" />
          <div className="mt-3 font-semibold text-[#5A5B5C]">Prüfen Sie Ihr Postfach.</div>
          <p className="mt-2 text-sm text-[#272525] leading-relaxed">
            Falls ein Konto mit dieser E-Mail-Adresse existiert, haben wir Ihnen einen
            Reset-Link geschickt. Der Link ist 60 Minuten gültig und kann nur einmal verwendet
            werden. Bitte schauen Sie auch im Spam-Ordner nach.
          </p>
          <p className="mt-4 text-xs text-[#5A5B5C]">
            Aus Sicherheitsgründen zeigen wir nicht an, ob die E-Mail-Adresse bei uns hinterlegt ist.
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-form">
          <div>
            <Label htmlFor="email" className="text-[#5A5B5C] text-sm font-semibold">
              E-Mail
            </Label>
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email-input"
              placeholder="ihre.email@beispiel.de"
              className="mt-1.5 h-11 border-[#EAEBE6] rounded-lg focus-visible:ring-[#A7B584] focus-visible:border-[#A7B584]"
            />
          </div>

          {err && (
            <div
              className="text-sm rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2"
              data-testid="forgot-error"
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="forgot-submit-btn"
            className="w-full bg-[#A7B584] hover:bg-[#8C9A69] text-white rounded-lg h-11 font-semibold"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Reset-Link senden <Mail className="ml-1.5 h-4 w-4" />
              </>
            )}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setErr("Reset-Link unvollständig — der Token fehlt in der URL.");
    }
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Das Passwort muss mindestens 6 Zeichen haben.");
      return;
    }
    if (password !== confirm) {
      setErr("Die beiden Passwörter stimmen nicht überein.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Neues Passwort vergeben."
      subtitle="Wählen Sie ein neues Passwort für Ihren Early-Tester-Zugang."
      footer={
        <>
          Doch zurück zur{" "}
          <Link
            to="/login"
            className="text-[#A7B584] hover:text-[#8C9A69] font-semibold"
            data-testid="reset-back-to-login"
          >
            Anmeldung
          </Link>
          ?
        </>
      }
    >
      {done ? (
        <div
          data-testid="reset-success"
          className="rounded-xl border border-[#A7B584]/40 bg-[#F4F5F2]/60 p-6"
        >
          <CheckCircle2 className="h-6 w-6 text-[#A7B584]" />
          <div className="mt-3 font-semibold text-[#5A5B5C]">
            Passwort erfolgreich geändert.
          </div>
          <p className="mt-2 text-sm text-[#272525]">
            Sie werden gleich automatisch zur Anmeldung weitergeleitet.
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
          <div>
            <Label htmlFor="new-pw" className="text-[#5A5B5C] text-sm font-semibold">
              Neues Passwort (min. 6 Zeichen)
            </Label>
            <Input
              id="new-pw"
              type="password"
              required
              minLength={6}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password-input"
              className="mt-1.5 h-11 border-[#EAEBE6] rounded-lg focus-visible:ring-[#A7B584] focus-visible:border-[#A7B584]"
            />
          </div>
          <div>
            <Label htmlFor="confirm-pw" className="text-[#5A5B5C] text-sm font-semibold">
              Passwort bestätigen
            </Label>
            <Input
              id="confirm-pw"
              type="password"
              required
              minLength={6}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-confirm-input"
              className="mt-1.5 h-11 border-[#EAEBE6] rounded-lg focus-visible:ring-[#A7B584] focus-visible:border-[#A7B584]"
            />
          </div>

          {err && (
            <div
              className="text-sm rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2"
              data-testid="reset-error"
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading || !token}
            data-testid="reset-submit-btn"
            className="w-full bg-[#A7B584] hover:bg-[#8C9A69] text-white rounded-lg h-11 font-semibold"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Passwort speichern <ArrowRight className="ml-1.5 h-4 w-4" />
              </>
            )}
          </Button>

          <div className="flex items-start gap-2 text-xs text-[#5A5B5C]">
            <ShieldCheck className="h-4 w-4 text-[#A7B584] mt-0.5 shrink-0" />
            <span>
              Reset-Links sind 60 Minuten gültig und können nur einmal verwendet werden.
            </span>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
